import { Box, Typography, Stack, Divider, useTheme, alpha } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TrendingUp, TrendingDown, SwapHoriz, Percent, ShowChart } from "@mui/icons-material";
import { useUtility } from "../../../core/hooks/useUtility";
import { useResponsive } from "../../../core/hooks/useResponsive";

const StatTile = ({ label, value, sub, icon: Icon, accent, dense = false }) => {
  const theme = useTheme();
  const iconSize = dense ? 28 : 36;
  return (
    <Box
      sx={{
        borderRadius: 2,
        p: dense ? 1 : 1.25,
        height: "100%",
        minWidth: 0,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.95),
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}` },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 0.5, mb: dense ? 0.5 : 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: dense ? "0.62rem" : undefined, minWidth: 0 }}
        >
          {label}
        </Typography>
        {Icon && (
          <Box
            sx={{
              width: iconSize,
              height: iconSize,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: alpha(accent ?? theme.palette.primary.main, 0.12),
              color: accent ?? theme.palette.primary.main,
            }}
          >
            <Icon sx={{ fontSize: dense ? 16 : 20 }} />
          </Box>
        )}
      </Box>
      <Typography
        variant={dense ? "h6" : "h5"}
        fontWeight={800}
        title={typeof value === "string" ? value : undefined}
        sx={{
          color: accent ?? "text.primary",
          lineHeight: 1.2,
          ...(dense
            ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontSize: "1.05rem" }
            : {}),
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: dense ? 0.25 : 0.5, display: "block", fontSize: dense ? "0.65rem" : undefined }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
};

// Browser-default grouping, exactly as the original inline expressions used.
const money = (n) => (typeof n === "number" ? `₹ ${n.toLocaleString()}` : n);

/** One half of the split headline: caption (+ optional pill), figure, footnote. */
const HeadlineFigure = ({ label, value, valueColor, pill, pillColor, footnote }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={0.75} rowGap={0.25}>
      <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ lineHeight: 1.6 }}>
        {label}
      </Typography>
      {pill != null && (
        <Box
          component="span"
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 999,
            fontSize: "0.72rem",
            fontWeight: 800,
            lineHeight: 1.7,
            whiteSpace: "nowrap",
            bgcolor: alpha(pillColor, 0.16),
            color: pillColor,
          }}
        >
          {pill}
        </Box>
      )}
    </Stack>
    <Typography
      fontWeight={900}
      title={typeof value === "string" ? value : undefined}
      sx={{
        mt: 0.25,
        letterSpacing: "-0.03em",
        lineHeight: 1.15,
        color: valueColor,
        fontSize: { xs: "1.35rem", sm: "1.6rem", md: "1.9rem" },
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
    {footnote && (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4, lineHeight: 1.35 }}>
        {footnote}
      </Typography>
    )}
  </Box>
);

const BTStatistics = ({ tradeStatistics, tradeBudget }) => {
  const theme = useTheme();
  const { profitColor, lossColor, getColor } = useUtility();
  const { isPhone } = useResponsive();

  if (!tradeStatistics) return null;

  const net = tradeStatistics.netProfit;
  const netNum = typeof net === "string" ? Number(net) : net;
  // Older API builds sent the literal string "NaN%" for a run with no trades; the current one sends "—".
  const successRate =
    typeof tradeStatistics.successRate === "string" && /^NaN/.test(tradeStatistics.successRate)
      ? "—"
      : tradeStatistics.successRate;

  // Net value = the capital the run deployed, plus what it made or lost on it.
  // BackTestBO sizes every entry off tradeBudget (buyQty = floor(tradeBudget / buyPrice))
  // and only ever holds one position at a time, so tradeBudget is the working capital
  // and netProfit / tradeBudget is the return on it.
  const budget = Number(tradeBudget);
  const hasBudget = Number.isFinite(budget) && budget > 0;
  // Rounded: netProfit already carries 2dp, so the raw sum can land on 26234.559999999998.
  const netValue = hasBudget ? Math.round((budget + (Number(netNum) || 0)) * 100) / 100 : null;
  const returnPct = hasBudget ? ((Number(netNum) || 0) / budget) * 100 : null;
  const pctColor = getColor(returnPct ?? 0, 0);
  const pctArrow = returnPct > 0 ? "▲" : returnPct < 0 ? "▼" : "•";

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          p: { xs: 1.5, md: 2 },
          mb: 1.25,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
            theme.palette.secondary?.main ?? theme.palette.primary.main,
            0.08
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Stack
          direction="row"
          spacing={{ xs: 1.25, sm: 2 }}
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: alpha(theme.palette.primary.main, 0.2) }}
            />
          }
        >
          {hasBudget && (
            <HeadlineFigure
              label="Net value"
              value={money(netValue)}
              valueColor="text.primary"
              pill={`${pctArrow} ${Math.abs(returnPct).toFixed(2)}%`}
              pillColor={pctColor}
              footnote={`on ${money(budget)} deployed`}
            />
          )}
          <HeadlineFigure
            label="Net profit"
            value={money(net)}
            valueColor={getColor(netNum, 0)}
            footnote={
              <>
                {tradeStatistics.totalTrades} trade{tradeStatistics.totalTrades === 1 ? "" : "s"}
                {" · "}
                <Box component="span" sx={{ color: profitColor, fontWeight: 700 }}>
                  {tradeStatistics.profitTrades}W
                </Box>
                {" / "}
                <Box component="span" sx={{ color: lossColor, fontWeight: 700 }}>
                  {tradeStatistics.lossTrades}L
                </Box>
              </>
            }
          />
        </Stack>
      </Box>

      {/* xs goes 2-up rather than 1-up: five full-width tiles was ~550px of scroll for
          five short figures. Closed trades takes the full row so nothing sits half-empty. */}
      <Grid container spacing={isPhone ? 1 : 1.25}>
        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <StatTile
            dense={isPhone}
            label="Total profit"
            value={money(tradeStatistics.totalProfit)}
            icon={TrendingUp}
            accent={profitColor}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <StatTile
            dense={isPhone}
            label="Total loss"
            value={money(tradeStatistics.totalLoss)}
            icon={TrendingDown}
            accent={lossColor}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 4 }}>
          <StatTile dense={isPhone} label="Success rate" value={successRate} icon={Percent} />
        </Grid>
        <Grid size={{ xs: 6, sm: 6, md: 6 }}>
          <StatTile
            dense={isPhone}
            label="P/L ratio"
            value={`${tradeStatistics.profitTrades} : ${tradeStatistics.lossTrades}`}
            sub="wins : losses"
            icon={SwapHoriz}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <StatTile
            dense={isPhone}
            label="Trades"
            value={tradeStatistics.totalTrades}
            sub={tradeStatistics.openTrades ? `${tradeStatistics.openTrades} still open (valued at last close)` : undefined}
            icon={ShowChart}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BTStatistics;
